// Approving several requests at once (869fgu7vp), for the Inbox's bar and the approvals table.
// A form marked data-bulk-approve-form owns checkboxes (name="ids", joined by the form attribute)
// and a button data-bulk-approve. A yes in bulk is for what the approver could read: every row
// carries a <template data-bulk-review="{id}" data-bulk-ok="true|false"> with its title and
// fields, or the reason it can't be listed. Approve counts only the rows that can be listed and,
// through the confirm dialog's data-confirm-details, lists each of them and says how many ticked
// rows are left out (a row not listed stays approvable from its own page; declining takes every
// row). The server refuses the same rows, whatever is posted. Optional parts of the form:
//   [data-bulk-count]   "3 selected · 1 to approve on its own", shown while something is ticked
//   [data-bulk-hint]    shown while nothing is ticked
//   [data-bulk-details] where the dialog's content is built (the button's data-confirm-details names it)
//   template[data-bulk-leftout] the callout that names the rows left out
//   data-bulk-reveal    on the form: its buttons show only while something is ticked
//   data-bulk-label     on the Approve button: its text, shown as "Approve 2" past one row
import { $, $$ } from './dom';

const FORM = 'form[data-bulk-approve-form]';

interface State {
	ticked: HTMLInputElement[];
	listed: HTMLInputElement[];
	left: HTMLInputElement[];
}

const boxesOf = (form: HTMLFormElement) => $$<HTMLInputElement>('input[name="ids"]').filter((box) => box.form === form);
const reviewOf = (box: HTMLInputElement) =>
	$<HTMLTemplateElement>(`template[data-bulk-review="${CSS.escape(box.value)}"]`);

function stateOf(form: HTMLFormElement): State {
	const ticked = boxesOf(form).filter((box) => box.checked);
	const listed = ticked.filter((box) => reviewOf(box)?.dataset.bulkOk === 'true');
	return { ticked, listed, left: ticked.filter((box) => !listed.includes(box)) };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function refresh(form: HTMLFormElement): void {
	const { ticked, listed, left } = stateOf(form);
	const approve = $<HTMLButtonElement>('[data-bulk-approve]', form);
	const reveal = form.hasAttribute('data-bulk-reveal');
	for (const hint of $$('[data-bulk-hint]', form)) hint.hidden = ticked.length > 0;
	for (const count of $$('[data-bulk-count]', form)) {
		count.hidden = ticked.length === 0;
		count.textContent =
			`${ticked.length} selected` +
			(left.length === 0
				? ''
				: listed.length === 0
					? ' · none can be approved together'
					: ` · ${left.length} to approve on its own`);
	}
	for (const button of $$<HTMLButtonElement>('button[name="decision"]', form)) {
		button.hidden = reveal && ticked.length === 0;
		button.disabled = button === approve ? listed.length === 0 : ticked.length === 0;
	}
	const base = approve?.dataset.bulkLabel;
	const label = approve && base ? [...approve.childNodes].reverse().find((n) => n.nodeType === Node.TEXT_NODE) : null;
	if (label) label.textContent = listed.length > 1 ? `${base} ${listed.length}` : base!;
}

// The dialog lists what each approved row does, and says which ticked rows it leaves out.
function fillDetails(form: HTMLFormElement, approve: HTMLElement): void {
	const details = $('[data-bulk-details]', form);
	if (!details) return;
	const { listed, left } = stateOf(form);
	const content = (box: HTMLInputElement) => [...(reviewOf(box)?.content.cloneNode(true).childNodes ?? [])];
	const nodes: Node[] = [];
	const note = $<HTMLTemplateElement>('template[data-bulk-leftout]', form)?.content.cloneNode(true) as
		DocumentFragment | undefined;
	if (note && left.length > 0) {
		$('[data-bulk-leftout-text]', note)!.textContent =
			`${plural(left.length, 'selected action is', 'selected actions are')} left out: ` +
			"their details can't be shown in full here. Open each one to approve it on its own.";
		nodes.push(note);
	}
	nodes.push(...listed.flatMap(content), ...left.flatMap(content));
	details.replaceChildren(...nodes);
	approve.setAttribute(
		'data-confirm',
		`Approve and execute ${listed.length === 1 ? 'this agent action' : `these ${listed.length} agent actions`}? Check what each one does.`,
	);
}

const refreshAll = () => $$<HTMLFormElement>(FORM).forEach(refresh);

export function initBulkApprove(): void {
	refreshAll();
	document.addEventListener('tableManagerReinitialized', refreshAll);
	// Back from the next page restores the boxes the submit disabled.
	window.addEventListener('pageshow', () => {
		for (const form of $$<HTMLFormElement>(FORM)) for (const box of boxesOf(form)) box.disabled = false;
		refreshAll();
	});
	document.addEventListener('change', (e) => {
		const form = (e.target as HTMLInputElement).form;
		if ((e.target as Element).matches('input[name="ids"]') && form?.matches(FORM)) refresh(form);
	});
	document.addEventListener('click', (e) => {
		const approve = (e.target as Element).closest<HTMLButtonElement>('[data-bulk-approve]');
		if (approve?.form?.matches(FORM)) fillDetails(approve.form, approve);
	});
	// A row that can't be listed is not posted with an approval (the server would skip it too).
	document.addEventListener('submit', (e) => {
		const form = e.target as HTMLFormElement;
		if (!form.matches(FORM) || (e as SubmitEvent).submitter?.getAttribute('value') !== 'approve') return;
		for (const box of stateOf(form).left) box.disabled = true;
	});
}
